import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ComposedChart,
} from 'recharts';
import { periodTarget, sharedTarget } from '../periodTarget';
import { showsOtd, showsOtif } from '../deliveryMetricMode';
import { performanceColor } from '../scheduled/shared';
import { KPICell, SectionFrame, PERF_MONEY_CLASS } from './perfCells';
import { CircularProgress } from '../CircularProgress';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';
import { usePreferences, useFormatters } from '../../../contexts/PreferencesContext';
import type { POLineDrilldownMode, WeekTrendPoint } from '../../../lib/api/po-line-dashboard';
import { POLineDrilldownModal } from './POLineDrilldownModal';
import { deliveryTabCounts, type POLinePeriodProps, type POLineDrilldownState } from './types';

const PALETTE = {
  otif: '#0f766e',
  otd:  '#0284c7',
  spend: '#2563eb',
  ncr: '#dc2626',
  ppk: '#0284c7',
  target: '#94a3b8',
};

function extractChartClick(raw: unknown): { weekLabel?: string; segment?: string } {
  if (!raw || typeof raw !== 'object') return {};
  const obj = raw as { activeLabel?: unknown; activePayload?: unknown };
  const ap = obj.activePayload;
  const first = Array.isArray(ap) ? ap[0] : null;
  let segment: string | undefined;
  if (first && typeof first === 'object') {
    const dk = (first as { dataKey?: unknown }).dataKey;
    if (typeof dk === 'string') segment = dk;
  }
  const lbl = obj.activeLabel;
  return { weekLabel: typeof lbl === 'string' ? lbl : undefined, segment };
}

/**
 * Per Week panel of the PO Line Dashboard — clone of the Scheduled Dashboard's
 * PerWeek with PO-line wording and PO-line drill-downs.
 */
export function POLinePerWeek({ data, formatCurrency, filters, onRefresh }: POLinePeriodProps) {
  const qualityLabel = useQualityMeasureLabel();
  const { preferences } = usePreferences();
  const { formatCompactCurrency } = useFormatters();
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0;

  const otifTarget = data.otif_target ?? null;  // FE-20: no target → no colour
  const otdTarget = data.otd_target ?? null;
  const deliveryMode = data.delivery_metric_mode;  // CR-8697: the helpers know 'none'
  const showOTIF = showsOtif(deliveryMode);
  const showOTD  = showsOtd(deliveryMode);
  const chartTarget = showOTIF ? otifTarget : otdTarget;

  const weeks = data.per_week.weeks;
  const trend = weeks.map((w) => {
    const hasDelivery = (w.delivery.total_due ?? 0) > 0 && !w.is_shutdown;
    return {
      week: w.label,
      shutdown: !!w.is_shutdown,
      otif: hasDelivery ? w.delivery.otif_pct : null,
      // CR-7312: OTD counts receipts, so its point depends on receipts, not on lines due.
      otd:  (w.delivery.otd_due ?? 0) > 0 && !w.is_shutdown ? w.delivery.otd_pct : null,
      received: w.cost.received_value,
      open: w.cost.open_value,
      ncrs: w.quality.ncrs,
      ppm: w.quality.ppm,
      target: periodTarget(w, deliveryMode, chartTarget),  // CR-6266: the week's own target
    };
  });
  // CR-6266: one full-width target line while every point agrees; a step where a range changes it.
  const flatTarget = sharedTarget(trend);

  const totalReceived  = weeks.reduce((a, w) => a + w.cost.received_value, 0);
  const totalCommitted = weeks.reduce((a, w) => a + w.cost.total_value, 0);
  const totalOpen      = weeks.reduce((a, w) => a + w.cost.open_value, 0);
  const totalAppeals   = weeks.reduce((a, w) => a + (w.delivery.appeals ?? 0), 0);
  const totalNcrs = trend.reduce((a, x) => a + x.ncrs, 0);
  const last = trend[trend.length - 1];
  const lastWeek = weeks[weeks.length - 1];
  const lastOtd = last?.otd ?? null;

  const otifWeekValues = trend.map((t) => t.otif).filter((v): v is number => v !== null && v !== undefined);
  const avgOtifWeeks = otifWeekValues.length;
  // FE-10: no week with anything due (shutdown, new supplier) → N/A, not a red 0%.
  const avgOtif = avgOtifWeeks ? otifWeekValues.reduce((a, v) => a + v, 0) / avgOtifWeeks : null;
  const shutdownWeeks = trend.filter((t) => t.shutdown).length;

  const windowStart = weeks[0]?.week_start ?? data.today;
  const windowEnd   = weeks[weeks.length - 1]?.week_end ?? data.today;

  const [modal, setModal] = useState<POLineDrilldownState | null>(null);

  const openWeek = useCallback((mode: POLineDrilldownMode, week: WeekTrendPoint) => {
    setModal({ mode, periodStart: week.week_start, periodEnd: week.week_end, periodGrain: 'week', periodLabel: `Week ${week.label}`, tabCounts: deliveryTabCounts(week.delivery) });
  }, []);
  const openAggregate = useCallback((mode: POLineDrilldownMode, label: string) => {
    setModal({ mode, periodStart: windowStart, periodEnd: windowEnd, periodGrain: 'week', periodLabel: label });
  }, [windowStart, windowEnd]);
  const openLatest = useCallback((mode: POLineDrilldownMode) => {
    if (!lastWeek) return;
    setModal({ mode, periodStart: lastWeek.week_start, periodEnd: lastWeek.week_end, periodGrain: 'week', periodLabel: `Latest week (${lastWeek.label})`, tabCounts: deliveryTabCounts(lastWeek.delivery) });
  }, [lastWeek]);
  const findWeek = useCallback((label: string | undefined) => {
    if (!label) return null;
    return weeks.find((w) => w.label === label) ?? null;
  }, [weeks]);

  const weekFromBar = (raw: unknown) => {
    const labelRaw = raw && typeof raw === 'object'
      ? (raw as { payload?: { week?: unknown }; week?: unknown }).week
        ?? (raw as { payload?: { week?: unknown } }).payload?.week
      : undefined;
    return typeof labelRaw === 'string' ? findWeek(labelRaw) : null;
  };
  const onCostSegmentClick = (segment: 'received' | 'open') => (raw: unknown) => {
    const w = weekFromBar(raw);
    if (w) openWeek(segment === 'open' ? 'open' : 'received', w);
  };
  const onNcrBarClick = (raw: unknown) => {
    const w = weekFromBar(raw);
    if (w) openWeek('ncrs', w);
  };

  const tileBase = 'cursor-pointer transition rounded-md hover:bg-slate-100/60 px-1 py-0.5';
  const onTileKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') (e.currentTarget as HTMLElement).click();
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Quality" accent="red">
          <div className="grid grid-cols-2 gap-3">
            <div className={tileBase} role="button" tabIndex={0} onClick={() => openLatest('ncrs')} onKeyDown={onTileKey} title="View NCRs raised in the latest week">
              <KPICell label={`${qualityLabel} (latest)`} value={<span className="text-2xl font-semibold tabular-nums">{last?.ppm == null ? 'N/A' : last.ppm.toLocaleString()}</span>} />
            </div>
            <div className={tileBase} role="button" tabIndex={0} onClick={() => openAggregate('ncrs', 'Last 12 weeks')} onKeyDown={onTileKey} title="View all NCRs raised in the last 12 weeks">
              <KPICell label="NCRs (12wk)" value={<span className="text-2xl font-semibold tabular-nums">{totalNcrs.toLocaleString()}</span>} />
            </div>
          </div>
        </SectionFrame>
        <SectionFrame title="Delivery" accent="green">
          <div className={`grid gap-3 ${showOTIF && showOTD ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {showOTIF && (
              <div
                className={tileBase} role="button" tabIndex={0}
                onClick={() => openAggregate('otif_due', 'Last 12 weeks')} onKeyDown={onTileKey}
                title={shutdownWeeks
                  ? `Mean of ${avgOtifWeeks} complete weeks — ${shutdownWeeks} shutdown week(s) excluded. Click to view the OTIF-eligible PO lines behind it.`
                  : `Mean of ${avgOtifWeeks} complete weeks. Click to view the OTIF-eligible PO lines behind it.`}
              >
                <KPICell
                  label="OTIF (12wk avg)"
                  value={<CircularProgress percentage={avgOtif} size={80} strokeWidth={6} color={performanceColor(avgOtif, otifTarget)} decimals={kpiDecimals} />}
                  foot={avgOtifWeeks === 12 ? undefined : `${avgOtifWeeks} of 12 weeks${shutdownWeeks ? ` · ${shutdownWeeks} shutdown` : ''}`}
                />
              </div>
            )}
            {showOTD && (
              <div className={tileBase} role="button" tabIndex={0} onClick={() => openLatest('otd_due')} onKeyDown={onTileKey} title="View the latest week's receipts — On time / Not on time">
                <KPICell
                  label="OTD (latest)"
                  value={<CircularProgress percentage={lastOtd} size={80} strokeWidth={6} color={performanceColor(lastOtd, otdTarget)} decimals={kpiDecimals} />}
                  // Customer review 11 Sep (item 32): show the count the
                  // ring divides, so "102%" can never hide behind a bare ring.
                  foot={lastWeek ? `${lastWeek.delivery.otd_met}/${lastWeek.delivery.otd_due ?? lastWeek.delivery.total_due} receipts on time` : undefined}
                />
              </div>
            )}
            <div className={tileBase} role="button" tabIndex={0} onClick={() => openAggregate('appeals', 'Last 12 weeks')} onKeyDown={onTileKey} title="View delivery appeals raised in the last 12 weeks">
              <KPICell label="Appeals (12wk)" value={<span className="text-2xl font-semibold tabular-nums">{totalAppeals.toLocaleString()}</span>} />
            </div>
          </div>
        </SectionFrame>
        <SectionFrame title="Cost" accent="blue">
          <div className="grid grid-cols-3 gap-3">
            <div className={tileBase} role="button" tabIndex={0} onClick={() => openAggregate('received', 'Last 12 weeks')} onKeyDown={onTileKey} title="View PO lines delivered against in the last 12 weeks">
              <KPICell label="Received (12wk)" value={<span className={PERF_MONEY_CLASS}>{formatCurrency(totalReceived)}</span>} foot={`of ${formatCurrency(totalCommitted)} due`} />
            </div>
            <div className={tileBase} role="button" tabIndex={0} onClick={() => openAggregate('open', 'Last 12 weeks')} onKeyDown={onTileKey} title="View PO lines still owing from the last 12 weeks">
              <KPICell label="Open Value" value={<span className={PERF_MONEY_CLASS}>{formatCurrency(totalOpen)}</span>} foot="still owed (12wk)" />
            </div>
            <div className={tileBase} role="button" tabIndex={0} onClick={() => setModal({ mode: 'open', periodStart: data.today, periodLabel: 'All forward (now)' })} onKeyDown={onTileKey} title="View all forward-dated open PO lines">
              <KPICell label="Portfolio Open" value={<span className={PERF_MONEY_CLASS}>{formatCurrency(data.portfolio.open_orders_value)}</span>} foot="all forward (now)" />
            </div>
          </div>
        </SectionFrame>
      </div>

      {(showOTIF || showOTD) && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">
              {showOTIF && showOTD ? 'OTIF & OTD' : showOTIF ? 'OTIF' : 'OTD'} — 12-Week Trend
            </CardTitle>
            <p className="text-[11px] text-muted-foreground">{chartTarget === null ? 'No target set' : `Target ${chartTarget}% (dashed)`} · click a point to drill in</p>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={trend} margin={{ top: 5, right: 10, bottom: 0, left: -10 }} style={{ cursor: 'pointer' }}
                  onClick={(raw: unknown) => {
                    const { weekLabel, segment } = extractChartClick(raw);
                    const w = findWeek(weekLabel);
                    if (!w) return;
                    let mode: POLineDrilldownMode;
                    // An OTD point opens on its denominator (review item 32).
                    if (!showOTIF) mode = 'otd_due';
                    else if (!showOTD) mode = 'otif_due';
                    else mode = segment === 'otif' ? 'otif_due' : 'otd_due';
                    openWeek(mode, w);
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} unit="%" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => `${Math.round(v)}%`} />
                  {flatTarget !== null
                    ? <ReferenceLine y={flatTarget} stroke={PALETTE.target} strokeDasharray="4 4" />
                    : <Line type="step" dataKey="target" stroke={PALETTE.target} strokeDasharray="4 4" dot={false} activeDot={false} legendType="none" tooltipType="none" name="Target %" />}
                  {showOTIF && <Line type="monotone" dataKey="otif" stroke={PALETTE.otif} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="OTIF %" />}
                  {showOTD && <Line type="monotone" dataKey="otd" stroke={PALETTE.otd} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} name="OTD %" />}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Weekly PO Line Value — Received vs Open</CardTitle>
            <p className="text-[11px] text-muted-foreground">Click a bar to view that week's lines · received (blue) vs open (grey)</p>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatCompactCurrency(v, data.currency)} />
                  <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => formatCurrency(v)} />
                  <Bar dataKey="received" stackId="cost" fill={PALETTE.spend} radius={[0, 0, 0, 0]} name="Received" cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onCostSegmentClick('received')} />
                  <Bar dataKey="open" stackId="cost" fill="#cbd5e1" radius={[3, 3, 0, 0]} name="Open" cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onCostSegmentClick('open')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-700">Quality — NCRs &amp; {qualityLabel}</CardTitle>
            <p className="text-[11px] text-muted-foreground">Bars: NCRs raised · Line: {qualityLabel} defective · click a bar to drill into NCRs</p>
          </CardHeader>
          <CardContent>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: '#e2e8f0', fillOpacity: 0.7 }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                  <Bar yAxisId="left" dataKey="ncrs" fill={PALETTE.ncr} radius={[3, 3, 0, 0]} name="NCRs" cursor="pointer" activeBar={{ stroke: '#0f172a', strokeWidth: 1.5 }} onClick={onNcrBarClick} />
                  <Line yAxisId="right" type="monotone" dataKey="ppm" stroke={PALETTE.ppk} strokeWidth={2.5} dot={{ r: 2 }} name={qualityLabel} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {modal && (
        <POLineDrilldownModal
          open
          onClose={() => setModal(null)}
          mode={modal.mode}
          filters={filters}
          periodStart={modal.periodStart}
          periodEnd={modal.periodEnd}
          periodGrain={modal.periodGrain}
          periodLabel={modal.periodLabel}
          currency={data.currency}
          tabCounts={modal.tabCounts}
          tabContext={modal.tabContext}
          onDataChanged={onRefresh}
        />
      )}
    </div>
  );
}
